import { arrayMove } from '@dnd-kit/sortable';
import { faFacebook } from '@fortawesome/free-brands-svg-icons/faFacebook';
import { faGithub } from '@fortawesome/free-brands-svg-icons/faGithub';
import { faInstagram } from '@fortawesome/free-brands-svg-icons/faInstagram';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons/faLinkedin';
import { faPinterest } from '@fortawesome/free-brands-svg-icons/faPinterest';
import { faReddit } from '@fortawesome/free-brands-svg-icons/faReddit';
import { faSnapchat } from '@fortawesome/free-brands-svg-icons/faSnapchat';
import { faSpotify } from '@fortawesome/free-brands-svg-icons/faSpotify';
import { faTelegram } from '@fortawesome/free-brands-svg-icons/faTelegram';
import { faTiktok } from '@fortawesome/free-brands-svg-icons/faTiktok';
import { faTwitch } from '@fortawesome/free-brands-svg-icons/faTwitch';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons/faWhatsapp';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons/faXTwitter';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons/faAngleDown';
import { faAngleUp } from '@fortawesome/free-solid-svg-icons/faAngleUp';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { faSave } from '@fortawesome/free-solid-svg-icons/faSave';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import clone from 'lodash/clone';
import React, { useEffect, useRef, useState } from 'react';
import {
  Controller,
  FieldArrayWithId,
  UseFieldArrayRemove,
  UseFieldArrayReplace,
  UseFieldArrayUpdate,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraIcon } from '../../../components/AtiraIcon';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Input } from '../../../components/Input';
import { Text } from '../../../components/Text';
import { TextWithTruncated } from '../../../components/TextWithTruncated';
import { SortableChild } from '../../../components/dnd/SortableChild';
import { atiraLinkTestIDs } from '../../../constants/test/atiralink.testIds';
import { CreateAtiraLinkDto } from '../../../model/atira-link/dto/CreateAtiraLinkDto';
import { EditAtiraLinkDto } from '../../../model/atira-link/dto/EditAtiraLinkDto';
import { Breakpoints } from '../../../theme/Breakpoints';
import { FontSize } from '../../../theme/FontSize';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { Tooltip } from '../../../utils/local-antd';
import { moveErrorMessage } from '../utils/moveSocialErrorMessage';

const socialMediaIcons = {
  WHATSAPP: faWhatsapp,
  INSTAGRAM: faInstagram,
  TWITTER: faXTwitter,
  FACEBOOK: faFacebook,
  YOUTUBE: faYoutube,
  TELEGRAM: faTelegram,
  TIKTOK: faTiktok,
  SNAPCHAT: faSnapchat,
  REDDIT: faReddit,
  LINKEDIN: faLinkedin,
  PINTEREST: faPinterest,
  SPOTIFY: faSpotify,
  TWITCH: faTwitch,
  GITHUB: faGithub,
};

const StyledSortable = styled(SortableChild)`
  & > div:nth-child(2) {
    top: 2.75rem;
    transform: translateY(-50%);
  }
`;

const SocialLinkHandleWrapper = styled(Flex)<{
  valid?: boolean;
  isEditing: boolean;
}>`
  align-items: center;
  gap: ${Spacing.s};
  padding: ${Spacing.s};
  border-radius: ${Rounded.lg};
  border: ${({ theme, valid }) =>
    `2px solid ${theme[valid ? 'danger' : 'main']}`};
  flex-direction: column;
  background-color: ${({ theme }) => theme.bgCanvas};
  margin-inline-start: ${Spacing.l};
  height: ${({ isEditing }) => (isEditing ? '9rem' : '7rem')};

  @media (min-width: ${Breakpoints.TABLET}) {
    flex-direction: row;
    height: 5.5rem;
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    margin-inline-start: ${Spacing.xxl};
  }
`;

const SocialHandleLinkWrapper = styled(Flex)`
  width: 100%;
  gap: ${Spacing.s};
`;

const LinkWrapper = styled(Flex)`
  padding-inline-start: calc(26.25px + ${Spacing.s}) !important;
`;

const ActionsWrapper = styled(Flex)`
  gap: ${Spacing.s};
  align-items: center;
  margin-inline-start: auto;
  margin-top: auto;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    flex-direction: column;
    margin-top: 0;
  }
`;

const StyledButton = styled(Button)`
  padding: ${Spacing.s};
`;

const StyledInput = styled(Input)`
  background-color: ${({ theme }) => theme.transparent};
  border: ${({ theme }) => `1px solid ${theme.fgPrimary}`} !important;
  color: ${({ theme }) => theme.fgPrimary};
`;

const EditableTitleInput = styled(StyledInput)`
  font-weight: bold;
  margin-bottom: 2px;
`;

const MoveButtonsWrapper = styled(Flex)<{ isEditing: boolean }>`
  flex-direction: column;
  justify-content: center;
  position: absolute;
  top: ${({ isEditing }) => (isEditing ? '4.5rem' : '3.5rem')};
  inset-inline-start: 0;
  transform: translateY(-50%);

  @media (min-width: ${Breakpoints.TABLET}) {
    top: 2.75rem;
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    display: none;
  }
`;

const MoveButton = styled(Button)`
  padding: 0;
  margin: 0;
  color: ${({ theme }) => theme.main};
  background-color: transparent;
`;

const ErrorMessage = styled(Text)`
  font-size: ${FontSize.s};
  color: ${({ theme }) => theme.danger};
  padding-inline-start: ${Spacing.l};

  @media (min-width: ${Breakpoints.DESKTOP}) {
    padding-inline-start: ${Spacing.xxl};
  }
`;

type SocialLinkProps = {
  social: FieldArrayWithId<
    CreateAtiraLinkDto | EditAtiraLinkDto,
    'socials',
    'id'
  >;
  update?: UseFieldArrayUpdate<
    CreateAtiraLinkDto | EditAtiraLinkDto,
    'socials'
  >;
  remove?: UseFieldArrayRemove;
  replace?: UseFieldArrayReplace<
    CreateAtiraLinkDto | EditAtiraLinkDto,
    'socials'
  >;
  fields?: FieldArrayWithId<
    CreateAtiraLinkDto | EditAtiraLinkDto,
    'socials',
    'id'
  >[];
  index: number;
  className?: string;
};

export const SocialLink: React.FC<SocialLinkProps> = ({
  social,
  update,
  remove,
  replace,
  fields = [],
  index,
  className,
}) => {
  const [editingSocialLinkID, setEditingSocialLinkID] = useState<string | null>(
    null,
  );

  const titleInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const {
    clearErrors,
    setError,
    formState: { errors },
    control,
    getValues,
    watch,
  } = useFormContext<CreateAtiraLinkDto | EditAtiraLinkDto>();

  const { t } = useTranslation();

  const socials = watch('socials');

  const canDrag = Boolean(socials?.length && socials.length >= 2);

  const onEditSocialLink = (index: number) => {
    const liveSocials = getValues('socials');

    const social = liveSocials?.[index];

    if (!social) {
      return;
    }

    if (social.label.trim() && social.url.trim()) {
      update?.(index, {
        label: social.label,
        url: social.url,
        handle: social.handle,
        order: social.order,
      });
    }
  };

  const onMoveUp = (movedItemId: string) => {
    const activeIndex = fields.findIndex((item) => item.id === movedItemId);
    const overIndex = activeIndex - 1;

    if (overIndex > -1) {
      moveErrorMessage(activeIndex, overIndex, setError, clearErrors, errors);

      const newSocials = arrayMove(
        clone(socials || []),
        activeIndex,
        overIndex,
      );

      replace?.(newSocials);
    }
  };

  const onMoveDown = (movedItemId: string) => {
    const activeIndex = fields.findIndex((item) => item.id === movedItemId);
    const overIndex = activeIndex + 1;

    if (overIndex < fields.length) {
      moveErrorMessage(activeIndex, overIndex, setError, clearErrors, errors);

      const newSocials = arrayMove(
        clone(socials || []),
        activeIndex,
        overIndex,
      );

      replace?.(newSocials);
    }
  };

  useEffect(() => {
    if (editingSocialLinkID && titleInputRefs.current[editingSocialLinkID]) {
      titleInputRefs.current[editingSocialLinkID]?.focus();
    }
  }, [editingSocialLinkID]);

  return (
    <StyledSortable key={social.id} id={social.id} disabled={!canDrag}>
      <MoveButtonsWrapper isEditing={editingSocialLinkID === social.id}>
        <MoveButton
          icon={faAngleUp}
          onClick={() => onMoveUp(social.id)}
          disabled={index === 0 || !canDrag}
        />

        <MoveButton
          icon={faAngleDown}
          onClick={() => onMoveDown(social.id)}
          disabled={index === fields.length - 1 || !canDrag}
        />
      </MoveButtonsWrapper>

      <Flex flexDirection="column" width={'100%'}>
        <SocialLinkHandleWrapper
          valid={Boolean(errors.socials?.[index]?.url?.message)}
          className={className}
          isEditing={editingSocialLinkID === social.id}
        >
          <SocialHandleLinkWrapper>
            <Flex flexDirection="column" flex={1}>
              <Flex alignItems="center" gap="s">
                <AtiraIcon
                  icon={socialMediaIcons[social.handle]}
                  size="2x"
                  color="iconPrimary"
                />

                {editingSocialLinkID === social.id ? (
                  <Controller
                    control={control}
                    name={`socials.${index}.label`}
                    render={({
                      field: { value, onChange, ref },
                      fieldState: { error },
                    }) => (
                      <EditableTitleInput
                        value={value}
                        onChange={onChange}
                        style={{ fontWeight: 'bold' }}
                        valid={!error}
                        ref={(el) => {
                          titleInputRefs.current[social.id] = el;
                          ref(el);
                        }}
                        onFocus={() => clearErrors(`socials.${index}.label`)}
                      />
                    )}
                  />
                ) : (
                  <TextWithTruncated
                    length={20}
                    textProps={{
                      fontWeight: '600',
                      wordBreak: 'break-word',
                      fontSize: 's',
                    }}
                  >
                    {social.label}
                  </TextWithTruncated>
                )}
              </Flex>

              <LinkWrapper>
                {editingSocialLinkID === social.id ? (
                  <Controller
                    control={control}
                    name={`socials.${index}.url`}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <StyledInput
                        value={value}
                        onChange={onChange}
                        valid={!error}
                        onFocus={() => clearErrors(`socials.${index}.url`)}
                        type="url"
                      />
                    )}
                  />
                ) : (
                  <TextWithTruncated
                    length={25}
                    textProps={{
                      wordBreak: 'break-all',
                      fontSize: 'xs',
                    }}
                  >
                    {social.url}
                  </TextWithTruncated>
                )}
              </LinkWrapper>
            </Flex>
          </SocialHandleLinkWrapper>

          <ActionsWrapper>
            <Tooltip
              title={
                editingSocialLinkID === social.id
                  ? t('common.save')
                  : t('common.edit')
              }
            >
              <StyledButton
                onClick={() =>
                  editingSocialLinkID === social.id
                    ? onEditSocialLink(index)
                    : setEditingSocialLinkID(social.id)
                }
                icon={editingSocialLinkID === social.id ? faSave : faPen}
                disabled={
                  editingSocialLinkID === social.id &&
                  (!watch(`socials.${index}.label`) ||
                    !watch(`socials.${index}.url`))
                }
              />
            </Tooltip>

            {editingSocialLinkID !== social.id ? (
              <Tooltip title={t('atira_link.create.socials.remove.tooltip')}>
                <StyledButton
                  onClick={() => remove?.(index)}
                  icon={faTrash}
                  dataTestId={atiraLinkTestIDs.content.socialMedia.remove(
                    social.id,
                  )}
                />
              </Tooltip>
            ) : null}
          </ActionsWrapper>
        </SocialLinkHandleWrapper>

        {Boolean(errors.socials?.[index]?.url?.message) ? (
          <ErrorMessage>{errors?.socials?.[index]?.url?.message}</ErrorMessage>
        ) : null}
      </Flex>
    </StyledSortable>
  );
};
